import { ApiAnalysisStatus, ApiExtractionStatus } from '../models/analysis-api.model';
import { ApiProcedureType, ApiTenderStatus } from '../models/tender-api.model';
import { ApiMatchStatus } from '../models/tender-matching-api.model';
import { ApiRequirementCategory, ApiRequirementModality, ApiRequirementRunStatus } from '../models/requirement-analysis-api.model';
import { ApiMinimumCriterionType, ApiObligatedParty, ApiObligationType, ApiRequirementThemePriority, ApiThemeTopicCategory } from '../models/requirement-theme-api.model';
import { ApiEvidenceChecklistStatus, ApiSuitabilityStatus } from '../models/requirement-suitability-evidence-api.model';

/**
 * The German UI language foundation (Phase 1 of the Vergabe-Suche rebrand — see the earlier UX/branding
 * analysis this implements). Deliberately minimal: a plain, typed dictionary, no i18n framework, no
 * runtime language switch, no `localStorage`. German is the product's only language for now; this exists
 * solely so fachliche Status-/Enum-/Reason-Code-Texte live in one place instead of being duplicated across
 * every component template — the same role `ROLE_LABELS`/`CATEGORY_OPTIONS`+`labelOf()` already play
 * elsewhere in this codebase, just centralized under one root instead of one file per feature.
 *
 * Nothing in `src/app/**` outside this file, `badges.ts` and a later Phase 2 wire these values into a
 * component's template yet — Phase 1 only builds and tests the mapping itself (task requirement: "noch
 * NICHT sämtliche Feature-Komponenten übersetzen").
 */

/** The one place the product name is spelled — everything else (TitleStrategy, footer, sidebar, landing page) reads it from here, never repeats the literal string. */
export const PRODUCT_NAME = 'Vergabe-Suche';

/**
 * `Tender.status` (the tender's own lifecycle state) — see `worker/src/models/tender.ts`'s
 * `TENDER_STATUSES` / `src/app/core/models/tender-api.model.ts`'s `ApiTenderStatus`. Deliberately its own
 * map, never merged with `PROCEDURE_TYPE_LABELS` below: both technical vocabularies happen to share the
 * string value `"open"`, but they answer different questions ("is this tender still accepting offers?"
 * vs. "which kind of award procedure is this?") and must never collapse into one German word by accident —
 * see the file-level warning in the task this implements: "Status 'open' ist nicht dasselbe wie Procedure
 * Type 'open'."
 */
export const TENDER_STATUS_LABELS: Record<ApiTenderStatus, string> = {
  open: 'Offen',
  closed: 'Geschlossen',
  awarded: 'Vergeben',
  cancelled: 'Aufgehoben',
  unknown: 'Unbekannt',
};

/**
 * `Tender.procedureType` (which award procedure this is) — see `ApiProcedureType`. Note
 * `procedureType: 'open'` becomes **"Offenes Verfahren"**, not "Offen" — the established German
 * procurement term for the procedure, not a restatement of the tender's own status.
 */
export const PROCEDURE_TYPE_LABELS: Record<ApiProcedureType, string> = {
  open: 'Offenes Verfahren',
  restricted: 'Nichtoffenes Verfahren',
  negotiated: 'Verhandlungsverfahren',
  competitive_dialogue: 'Wettbewerblicher Dialog',
  innovation_partnership: 'Innovationspartnerschaft',
  design_contest: 'Planungswettbewerb',
  other: 'Sonstiges Verfahren',
};

/**
 * `TenderMatchResult.status` (the Matching Engine's own relevance verdict) — see
 * `worker/src/models/tender-matching.ts`'s `MatchStatus` / `core/models/tender-matching-api.model.ts`'s
 * `ApiMatchStatus`. Phase 2.3: deliberately neutral, relevance-only language — never "geeignet"/
 * "ungeeignet"/"empfohlen"/"nicht empfohlen"/"bewerben"/"nicht bewerben" (task requirement). The Matching
 * Engine measures relevance, not a final bid/no-bid decision, and a `NO_MATCH` tender is still shown with
 * its real score, never hidden or worded as rejected. Deliberately its own map, never merged with
 * `TENDER_STATUS_LABELS` above — same "two different vocabularies, never one collapsed German word" rule
 * that already governs `TENDER_STATUS_LABELS` vs. `PROCEDURE_TYPE_LABELS` (see that pair's own comment):
 * a tender's lifecycle status and its match status are independent concepts and must stay independently
 * filterable/labelled (task requirement — see `DiscoverTenderStore`'s separate `tenderStatus`/`matchStatus`
 * signals).
 */
export const MATCH_STATUS_LABELS: Record<ApiMatchStatus, string> = {
  MATCH: 'Relevant',
  NO_MATCH: 'Geringere Relevanz',
  UNKNOWN: 'Nicht bestimmbar',
};

/**
 * Fixed Discover/relevance UI microcopy (Phase 2.3) — kept centralized here (like every other controlled
 * label in this file) rather than as inline template literals, so it is covered by the same "never mentions
 * the old product name, never says the bare English word 'Tender'" tests as the rest of this file.
 * `unknown` is shown instead of a fabricated 0% whenever `TenderMatchResult.score` is `null` (task
 * requirement — see `shared/format.ts`'s `formatRelevance()`, the one place this is used).
 */
/**
 * Known TED language codes (ISO 639-1), translated for the Tender Detail page's "Sprache" field (Phase
 * 2.5A UI cleanup) — a raw code like "de"/"en" is never shown to a customer directly. Deliberately NOT
 * exhaustive: only the values this application has actually observed (task requirement). `languageLabel()`
 * (`shared/format.ts`) falls back to the original, unmodified value for any code not listed here — never a
 * fabricated translation.
 */
export const LANGUAGE_LABELS: Record<string, string> = {
  de: 'Deutsch',
  en: 'Englisch',
};

/**
 * `ProcurementAnalysis.status` (Phase 3.2/3.3, "Vergabeanalyse") — see
 * `worker/src/models/procurement-analysis.ts`'s `ANALYSIS_STATUSES` / `core/models/analysis-api.model.ts`'s
 * `ApiAnalysisStatus`. Deliberately its own map (one entry today) rather than merged into
 * `TENDER_STATUS_LABELS`/`MATCH_STATUS_LABELS` above — a third, independent vocabulary, same "never one
 * collapsed German word across different technical meanings" rule those two already follow.
 */
export const ANALYSIS_STATUS_LABELS: Record<ApiAnalysisStatus, string> = {
  draft: 'Entwurf',
};

/**
 * `AnalysisDocument.extractionStatus` (Phase 3.5D.1 text extraction) — see
 * `worker/src/models/analysis-document.ts` / `core/models/analysis-api.model.ts`'s `ApiExtractionStatus`.
 * Deliberately its own map, same "never one collapsed German word across different vocabularies" rule.
 */
export const EXTRACTION_STATUS_LABELS: Record<ApiExtractionStatus, string> = {
  pending: 'Noch nicht extrahiert',
  extracting: 'Extraktion läuft',
  ready: 'Extrahiert',
  no_text: 'Kein Text erkannt',
  unsupported: 'Dateiformat nicht unterstützt',
  failed: 'Extraktion fehlgeschlagen',
};

/**
 * Phase 3.6A/B — `AnalysisRequirementRun.status` (worker/src/models/requirement-analysis.ts's
 * `REQUIREMENT_RUN_STATUSES` / `core/models/requirement-analysis-api.model.ts`'s `ApiRequirementRunStatus`).
 * `ready`/`partial` wording tightened in Phase 3.6E (task's own explicit example wording) — `partial` no
 * longer reads as an incomplete/failed-sounding state ("Teilweise abgeschlossen"); it is still a genuinely
 * terminal status (unchanged — `AnalysisDetailStore.hasActiveRuns()` never treats it as active), just phrased
 * so a customer reads it as "done, with a caveat" rather than "not finished". See `REQUIREMENT_RUN_PARTIAL_HINT`
 * below for the accompanying explanatory sentence.
 */
export const REQUIREMENT_RUN_STATUS_LABELS: Record<ApiRequirementRunStatus, string> = {
  queued: 'Wartet auf Verarbeitung',
  processing: 'Analyse läuft',
  ready: 'Abgeschlossen',
  partial: 'Abgeschlossen mit Hinweisen',
  failed: 'Analyse fehlgeschlagen',
};

/**
 * Phase 3.6E — the customer-facing explanation shown for a `partial` run, REPLACING the raw backend
 * `error_reason` string (e.g. "one or more chunks had rejected or unrecoverable candidates") that the
 * pre-3.6E UI showed verbatim (task requirement: "Der technische error_reason darf nicht als ungefilterte
 * englische Backend-Meldung prominent im Kundeninterface erscheinen"). The underlying `error_reason` value
 * itself is unchanged on the backend and still exists in the API response — this is purely a display
 * decision, nothing here reads or forwards that raw field into the UI.
 */
export const REQUIREMENT_RUN_PARTIAL_HINT = 'Die Analyse wurde abgeschlossen. Einzelne Inhalte konnten möglicherweise nicht vollständig ausgewertet werden. Bitte prüfen Sie wichtige Anforderungen anhand der Originalunterlagen.';

/**
 * Phase 3.6B/C — `AnalysisRequirement.category` (database/migrations/0009_analysis_requirements.sql's own
 * CHECK constraint / `core/models/requirement-analysis-api.model.ts`'s `ApiRequirementCategory`).
 */
export const REQUIREMENT_CATEGORY_LABELS: Record<ApiRequirementCategory, string> = {
  personnel: 'Personal',
  technical: 'Technik',
  organizational: 'Organisation',
  security_compliance: 'Sicherheit & Compliance',
  certification: 'Zertifizierung',
  reference_experience: 'Referenzen & Erfahrung',
  deadline_time: 'Fristen & Termine',
  location_onsite: 'Standort & Vor-Ort-Einsatz',
  commercial_financial: 'Kommerziell & Finanziell',
  documentation: 'Dokumentation',
  contractual_condition: 'Vertragsbedingung',
  information: 'Information',
};

/**
 * Phase 3.6B/C — `AnalysisRequirement.modality` ("MUST-Klassifizierung" — same CHECK constraint source as
 * `REQUIREMENT_CATEGORY_LABELS` above). `UNKNOWN` is a real, reachable value (the model could not determine
 * a modality) — never hidden or silently relabelled as something more definite than it is.
 */
export const REQUIREMENT_MODALITY_LABELS: Record<ApiRequirementModality, string> = {
  MUST: 'Pflicht (MUSS)',
  SHOULD: 'Empfohlen (SOLL)',
  MAY: 'Optional (KANN)',
  CONDITIONAL: 'Bedingt',
  INFORMATIONAL: 'Information',
  UNKNOWN: 'Unklar',
};

/**
 * Phase 3.6F Etappe 3 — `AnalysisRequirementThemeRun.status`. Deliberately NOT a second map: the real backend
 * status vocabulary (`ApiRequirementThemeRunStatus`) is the exact same five-value union as
 * `ApiRequirementRunStatus` (`queued/processing/ready/partial/failed`) — `REQUIREMENT_RUN_STATUS_LABELS`
 * above is reused unchanged (same "never one collapsed German word duplicated for an identical vocabulary"
 * rule this file already follows elsewhere, just applied in reverse: reuse, don't duplicate, when the
 * vocabulary truly is identical).
 */

/**
 * Phase 3.6F Etappe 3 — `AnalysisRequirementClassification.obligationType` / `AnalysisRequirementTheme
 * .obligationEmphasis` (database/migrations/0017/0019's own CHECK constraints). Wording taken directly from
 * the Phase 3.6F task's own §B2 list — never re-translated independently.
 */
export const OBLIGATION_TYPE_LABELS: Record<ApiObligationType, string> = {
  formal_eligibility_criterion: 'Formales Eignungskriterium',
  minimum_requirement: 'Mindestanforderung',
  performance_obligation: 'Leistungspflicht',
  contractual_obligation: 'Vertragliche Verpflichtung',
  expectation_quality: 'Erwartung / Qualitätsanforderung',
  evidence_obligation: 'Nachweispflicht',
  information_background: 'Information / Hintergrund',
};

/** Phase 3.6F Etappe 3 — "wer ist verpflichtet" (task §B2: "Verpflichtung des Bieters / des Auftragnehmers während der Leistungserbringung / des Auftraggebers / allgemeine Leistungsbeschreibung"). */
export const OBLIGATED_PARTY_LABELS: Record<ApiObligatedParty, string> = {
  bidder: 'Bieter',
  contractor_during_performance: 'Auftragnehmer (während der Leistungserbringung)',
  contracting_authority: 'Auftraggeber',
  general_description: 'Allgemeine Leistungsbeschreibung',
};

/** Phase 3.6F Etappe 3 — `AnalysisRequirementTheme.priority` (database/migrations/0019). */
export const REQUIREMENT_THEME_PRIORITY_LABELS: Record<ApiRequirementThemePriority, string> = {
  critical: 'Kritisch',
  high: 'Hoch',
  medium: 'Mittel',
  low: 'Niedrig',
};

/**
 * Phase 3.6H (task §Aufgabe 1: "Kontrolliertes Themenvokabular") — 7 of these 9 keys are the EXACT SAME
 * strings `REQUIREMENT_CATEGORY_LABELS` above already uses; their labels are deliberately IDENTICAL (never a
 * second, independently-drifting German word for the same concept — task's own "Backend und Angular
 * verwenden dieselben Werte"). `eligibility`/`other` are new, theme-only categories with their own labels.
 */
export const THEME_TOPIC_CATEGORY_LABELS: Record<ApiThemeTopicCategory, string> = {
  personnel: REQUIREMENT_CATEGORY_LABELS.personnel,
  technical: REQUIREMENT_CATEGORY_LABELS.technical,
  organizational: REQUIREMENT_CATEGORY_LABELS.organizational,
  documentation: REQUIREMENT_CATEGORY_LABELS.documentation,
  security_compliance: REQUIREMENT_CATEGORY_LABELS.security_compliance,
  contractual_condition: REQUIREMENT_CATEGORY_LABELS.contractual_condition,
  commercial_financial: REQUIREMENT_CATEGORY_LABELS.commercial_financial,
  eligibility: 'Eignung',
  information: 'Allgemeine Informationen',
  other: 'Sonstiges',
};

/** Phase 3.6F Etappe 3 — `AnalysisRequirementMinimumCriterion.criterionType` (database/migrations/0018, task §B3's own enumeration). */
export const MINIMUM_CRITERION_TYPE_LABELS: Record<ApiMinimumCriterionType, string> = {
  certification: 'Zertifizierung',
  experience_years: 'Berufserfahrung',
  min_employees: 'Mindestanzahl Mitarbeiter',
  min_revenue: 'Mindestumsatz',
  reference_project: 'Referenzprojekt',
  technical_qualification: 'Technische Qualifikation',
  security_requirement: 'Sicherheitsanforderung',
  insurance: 'Versicherungsnachweis',
  language_skill: 'Sprachkenntnisse',
  deadline: 'Frist',
  response_time: 'Reaktionszeit',
  other: 'Sonstige Mindestbedingung',
};

/** Phase 3.6F/3.6G Etappe 4 — "Eignungsprüfung" (database/migrations/0021). `nicht_erfuellt` is deliberately NOT phrased as a hard rejection ("Ausgeschlossen") — a single failed criterion is one data point for the customer's own bid/no-bid decision, never an automatic verdict this page renders. */
export const SUITABILITY_STATUS_LABELS: Record<ApiSuitabilityStatus, string> = {
  erfuellt: 'Erfüllt',
  nicht_erfuellt: 'Nicht erfüllt',
  unklar: 'Unklar',
  nicht_bewertet: 'Nicht bewertet',
  nicht_anwendbar: 'Nicht anwendbar',
};

/** Phase 3.6F/3.6G Etappe 4 — "Nachweis-Checkliste" (database/migrations/0022). */
export const EVIDENCE_CHECKLIST_STATUS_LABELS: Record<ApiEvidenceChecklistStatus, string> = {
  offen: 'Offen',
  vorhanden: 'Vorhanden',
  in_bearbeitung: 'In Bearbeitung',
  nicht_erforderlich: 'Nicht erforderlich',
};

export const RELEVANCE_LABELS = {
  scoreSuffix: 'Relevanz',
  unknown: 'Relevanz nicht bestimmbar',
  bestMatchingLot: 'Passendstes Los',
  /** Phase 2.5A — shown on the Tender Detail page when `GET /tenders/:id/match` itself failed (the tender's own data still loaded fine). A temporary relevance outage must never look like the tender was rejected or is unavailable. */
  unavailable: 'Relevanz derzeit nicht verfügbar',
} as const;
